import { useState, useEffect } from "react";
import { Download, X, Smartphone } from "lucide-react";

export default function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia("(display-mode: standalone)").matches) return;

    // Detect iOS
    const ua = navigator.userAgent;
    const isiOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    setIsIOS(isiOS);

    // Check if user dismissed recently (24h cooldown)
    const dismissed = localStorage.getItem("pwa-dismissed");
    if (dismissed && Date.now() - parseInt(dismissed) < 86400000) return;

    if (isiOS) {
      // Show iOS install guide after 5s
      const t = setTimeout(() => setShowBanner(true), 5000);
      return () => clearTimeout(t);
    }

    // Android/Desktop: listen for beforeinstallprompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setTimeout(() => setShowBanner(true), 3000);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  // Register service worker
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("/sw.js").catch(() => {});
    }
  }, []);

  const handleInstall = async () => {
    if (isIOS) {
      setShowIOSGuide(true);
      return;
    }
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const result = await deferredPrompt.userChoice;
    if (result.outcome === "accepted") {
      setShowBanner(false);
    }
    setDeferredPrompt(null);
  };

  const dismiss = () => {
    setShowBanner(false);
    setShowIOSGuide(false);
    localStorage.setItem("pwa-dismissed", Date.now().toString());
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Install Banner */}
      <div
        data-testid="pwa-install-banner"
        className="fixed bottom-4 left-4 right-4 z-[9999] mx-auto max-w-md animate-in slide-in-from-bottom duration-500"
      >
        <div className="bg-gradient-to-r from-[#0B1120] to-[#131B2E] border border-[#0099CC]/30 rounded-2xl p-4 shadow-2xl shadow-cyan-500/10 backdrop-blur-xl">
          <button onClick={dismiss} className="absolute top-3 right-3 text-gray-500 hover:text-white transition-colors" data-testid="pwa-dismiss">
            <X className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-[#0099CC]/20 flex items-center justify-center flex-shrink-0">
              <Smartphone className="h-6 w-6 text-[#0099CC]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-semibold text-sm">Installer GLOTECH OS</p>
              <p className="text-gray-400 text-xs mt-0.5">Acces rapide depuis votre ecran d'accueil</p>
            </div>
            <button
              onClick={handleInstall}
              data-testid="pwa-install-btn"
              className="bg-[#0099CC] hover:bg-[#00AADD] text-white text-xs font-bold px-4 py-2 rounded-xl transition-all flex items-center gap-1.5 flex-shrink-0"
            >
              <Download className="h-3.5 w-3.5" />
              Installer
            </button>
          </div>
        </div>
      </div>

      {/* iOS Guide Modal */}
      {showIOSGuide && (
        <div className="fixed inset-0 z-[10000] bg-black/70 backdrop-blur-sm flex items-end justify-center p-4" onClick={dismiss}>
          <div className="bg-[#131B2E] border border-[#2A3441] rounded-2xl p-6 max-w-sm w-full animate-in slide-in-from-bottom duration-300" onClick={e => e.stopPropagation()}>
            <h3 className="text-white font-bold text-lg mb-4">Installer sur iPhone / iPad</h3>
            <div className="space-y-4 text-sm text-gray-300">
              <div className="flex items-start gap-3">
                <span className="bg-[#0099CC]/20 text-[#0099CC] rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 text-xs font-bold">1</span>
                <p>Appuyez sur le bouton <strong className="text-white">Partager</strong> (icone carree avec une fleche vers le haut) en bas de Safari</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="bg-[#0099CC]/20 text-[#0099CC] rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 text-xs font-bold">2</span>
                <p>Faites defiler et appuyez sur <strong className="text-white">"Sur l'ecran d'accueil"</strong></p>
              </div>
              <div className="flex items-start gap-3">
                <span className="bg-[#0099CC]/20 text-[#0099CC] rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 text-xs font-bold">3</span>
                <p>Appuyez sur <strong className="text-white">"Ajouter"</strong> en haut a droite</p>
              </div>
            </div>
            <button onClick={dismiss} className="mt-6 w-full bg-[#0099CC] text-white font-bold py-2.5 rounded-xl text-sm">Compris !</button>
          </div>
        </div>
      )}
    </>
  );
}
